/**
 * Manages payload size. Strategy:
 * 1. Compress screenshots (JPEG at lower quality if too large)
 * 2. If still over limit, drop element screenshots
 * 3. If still over, drop manual screenshots
 * 4. If still over, drop viewport screenshot
 * 5. If still over, reduce console/network counts
 *
 * Total payload hard cap: 5MB
 */

const MAX_PAYLOAD_BYTES = 12 * 1024 * 1024; // 12MB
const MAX_SCREENSHOT_BYTES = 2 * 1024 * 1024; // 2MB per screenshot

/**
 * Compress a base64 data URL by converting to JPEG with lower quality.
 * Returns a compressed data URL or the original if compression fails.
 */
export async function compressScreenshot(
  dataUrl: string,
  maxBytes: number = MAX_SCREENSHOT_BYTES,
  quality = 0.7,
): Promise<string> {
  if (!dataUrl) return dataUrl;

  // Check current size
  const currentSize = estimateBase64Size(dataUrl);
  if (currentSize <= maxBytes) return dataUrl;

  return new Promise<string>((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;

      // Resize if very large
      const maxDim = 1920;
      if (width > maxDim || height > maxDim) {
        const ratio = Math.min(maxDim / width, maxDim / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(dataUrl);
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);

      // Try JPEG compression
      let result = canvas.toDataURL('image/jpeg', quality);

      // If still too big, reduce further
      if (estimateBase64Size(result) > maxBytes && quality > 0.3) {
        result = canvas.toDataURL('image/jpeg', 0.3);
      }

      // If STILL too big, resize down more
      if (estimateBase64Size(result) > maxBytes) {
        canvas.width = Math.round(width * 0.5);
        canvas.height = Math.round(height * 0.5);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        result = canvas.toDataURL('image/jpeg', 0.3);
      }

      resolve(result);
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

export function estimateBase64Size(dataUrl: string): number {
  // Remove data URL prefix
  const base64 = dataUrl.split(',')[1] || dataUrl;
  return Math.round(base64.length * 0.75);
}

export function estimatePayloadSize(payload: unknown): number {
  return new TextEncoder().encode(JSON.stringify(payload)).length;
}

export async function enforcePayloadLimit(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  let size = estimatePayloadSize(payload);
  if (size <= MAX_PAYLOAD_BYTES) return payload;

  const screenshots = payload.screenshots as Record<string, unknown> | undefined;

  // Step 1: Drop element screenshots
  if (screenshots && Array.isArray(screenshots.elements) && screenshots.elements.length > 0) {
    screenshots.elements = [];
    size = estimatePayloadSize(payload);
    if (size <= MAX_PAYLOAD_BYTES) return payload;
  }

  // Step 2: Trim manual screenshots one-by-one
  if (screenshots && Array.isArray(screenshots.manual) && screenshots.manual.length > 0) {
    const manual = screenshots.manual as unknown[];
    while (manual.length > 0) {
      manual.pop();
      size = estimatePayloadSize(payload);
      if (size <= MAX_PAYLOAD_BYTES) return payload;
    }
  }

  // Step 3: Drop viewport screenshot
  if (screenshots && screenshots.viewport) {
    screenshots.viewport = '[removed - payload too large]';
    size = estimatePayloadSize(payload);
    if (size <= MAX_PAYLOAD_BYTES) return payload;
  }

  // Step 4: Reduce console logs
  const diagnostics = payload.diagnostics as Record<string, unknown> | undefined;
  if (diagnostics && Array.isArray(diagnostics.console)) {
    diagnostics.console = (diagnostics.console as unknown[]).slice(-20);
    size = estimatePayloadSize(payload);
    if (size <= MAX_PAYLOAD_BYTES) return payload;
  }

  // Step 5: Reduce network requests
  if (diagnostics && Array.isArray(diagnostics.network)) {
    diagnostics.network = (diagnostics.network as unknown[]).slice(-10);
  }

  return payload;
}
